import * as RadPopover from '@radix-ui/react-popover'
import React, { useRef, useState } from 'react'
import styled, { css } from 'styled-components'

import { Separator, useBorderBoxSize } from '@blade/ui-kit'
import { ArrowDown2Icon } from '@root/shared/ui-kit/icons'

export type SelectProps<T> = {
  disabled?: boolean
  items: ReadonlyArray<T>
  value: NoInfer<T> | undefined
  label: string
  getId: (item: NoInfer<T>) => string
  onChange: (item: NoInfer<T> | undefined) => void
  renderOption: (item: NoInfer<T>) => string
  renderValue: (item: NoInfer<T>) => string
}

export const Select = <T,>({
  items,
  value,
  label,
  getId,
  onChange,
  renderOption,
  renderValue,
  disabled,
}: SelectProps<T>) => {
  const triggerRef = useRef<HTMLButtonElement>(null)
  const minWidth = useBorderBoxSize(triggerRef).inlineSize
  const [open, setOpen] = useState<boolean>(false)

  const onSelect = (item: T | undefined) => {
    setOpen(false)
    onChange(item)
  }

  return (
    <Root onOpenChange={setOpen}>
      <Trigger asChild open={open} ref={triggerRef}>
        <RootButton disabled={disabled}>
          <Body>
            <Label $withValue={!!value}>{label}</Label>
            {value ? <Value>{renderValue(value)}</Value> : undefined}
          </Body>
          <Icon $isOpen={open}>
            <ArrowDown2Icon size={20} />
          </Icon>
        </RootButton>
      </Trigger>
      <RadPopover.Portal>
        <Content
          side="bottom"
          sideOffset={4}
          align="start"
          style={{ minWidth }}
        >
          {items.map((item, index) => (
            <React.Fragment key={getId(item)}>
              <Item onClick={() => onSelect(item)}>{renderOption(item)}</Item>
              {index < items.length - 1 ? <ItemSeparator /> : undefined}
            </React.Fragment>
          ))}
        </Content>
      </RadPopover.Portal>
    </Root>
  )
}

const Root = styled(RadPopover.Root)<{}>`
  display: grid;
  gap: 10px;
`

const Trigger = styled(RadPopover.Trigger)<{
  open: boolean
}>``

const RootButton = styled.button`
  all: unset;
  box-sizing: border-box;
  cursor: pointer;
  border-radius: 10px;

  background: var(--appearanceA-7);
  border: 1px solid transparent;
  transition: all 300ms ease;

  width: 100%;
  color: var(--grady-10);
  padding: 3px 3px 3px 16px;

  display: grid;
  grid-template-columns: 1fr auto;
  justify-content: space-between;
  align-items: center;
  gap: 10px;

  &:hover:enabled {
    background: ${(props) => props.theme.color.black2[30]};

    background: var(--appearance-1);
    border-color: var(--background-4);
  }
  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`

const Body = styled.span`
  display: grid;
`

const Label = styled.span<{ $withValue: boolean }>`
  font-weight: 500;

  ${({ $withValue }) =>
    $withValue
      ? css`
          color: var(--text-10);
          font-size: 11px;
        `
      : css`
          color: var(--text-12);
          font-size: 12px;
        `}
`

const Value = styled.span`
  color: var(--text-12);
  font-size: 13px;

  &:empty + .label {
    transform: none;
  }
`

const Icon = styled.span<{ $isOpen: boolean }>`
  display: grid;
  align-items: center;
  justify-content: center;
  padding: 5px;
  color: var(--text-12);
  background: var(--backgroundA-3);
  border-radius: 6px;
  height: 32px;
  width: 26px;
  margin-left: auto;
  transform: rotate(${(_) => (_.$isOpen ? 180 : 0)}deg);
`

const Content = styled(RadPopover.Content)`
  background: var(--background-1);
  color: var(--text-10);

  overflow: hidden;
  border-radius: 10px;

  display: grid;

  padding: 3px;
  gap: 2px;
`

const Item = styled(RadPopover.Close)`
  all: unset;
  cursor: pointer;
  padding: 6px;
  transition: all 100ms ease-in;
  font-size: 12px;
  font-weight: 500;
  line-height: normal;
  border-radius: 8px;

  &:hover {
    color: var(--appearance-inv-1);
    background: var(--backgroundA-2);
  }
`

const ItemSeparator = styled(Separator)`
  margin: 0 10px;
  background: var(--backgroundA-2);
`
